import { Rgl, RglBreakpoint, WorkspaceElement, WorkspaceLayout } from 'constants/types'

/** Límite del backend para el campo `configuration` (Joi: length <= 7000) */
export const CONFIGURATION_MAX_LENGTH = 7000

const RGL_BREAKPOINTS: RglBreakpoint[] = ['lg', 'md', 'sm', 'xs']

const toNumberOrUndefined = (value: unknown): number | undefined => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

/**
 * Espejo legacy compacto: solo los campos que la app lee al cargar
 * (posición/tamaño + restricciones usadas por el botón ensanchar/angostar).
 * Los drags de RGL emiten el LayoutItem completo (moved, isDraggable, resizeHandles...)
 * y persistirlo infla `configuration` hasta superar el límite del backend.
 */
const toCompactRgl = (rgl: Rgl): Rgl => {
  const compact: Rgl = {
    x: toNumberOrUndefined(rgl.x) ?? 0,
    y: toNumberOrUndefined(rgl.y) ?? 0,
    w: toNumberOrUndefined(rgl.w) ?? 4,
    h: toNumberOrUndefined(rgl.h) ?? 8
  }
  const minW = toNumberOrUndefined(rgl.minW)
  const minH = toNumberOrUndefined(rgl.minH)
  const maxW = toNumberOrUndefined(rgl.maxW)
  if (minW !== undefined) compact.minW = minW
  if (minH !== undefined) compact.minH = minH
  if (maxW !== undefined) compact.maxW = maxW
  if (rgl.static) compact.static = true
  return compact
}

/** Por breakpoint solo se necesita el rect: el resto se re-deriva al generar layouts */
const toCompactRect = (rgl: Rgl): Rgl => ({
  x: toNumberOrUndefined(rgl.x) ?? 0,
  y: toNumberOrUndefined(rgl.y) ?? 0,
  w: toNumberOrUndefined(rgl.w) ?? 4,
  h: toNumberOrUndefined(rgl.h) ?? 8
})

const compactLayoutForPersistence = (layout: WorkspaceLayout, includeBreakpoints: boolean): WorkspaceLayout =>
  layout.map((item) => {
    const next: WorkspaceElement = { ...item }
    if (item.rgl) {
      const compact = toCompactRgl(item.rgl)
      if (item.maximized !== 1) {
        delete compact.static
        const maxW = compact.maxW ?? compact.w
        if (compact.minW !== undefined && maxW !== undefined && compact.minW >= maxW) {
          delete compact.minW
        }
      }
      next.rgl = compact
    }
    if (includeBreakpoints && item.rglByBreakpoint) {
      const compactByBreakpoint: Partial<Record<RglBreakpoint, Rgl>> = {}
      for (const breakpoint of RGL_BREAKPOINTS) {
        const stored = item.rglByBreakpoint[breakpoint]
        if (stored) compactByBreakpoint[breakpoint] = toCompactRect(stored)
      }
      next.rglByBreakpoint = Object.keys(compactByBreakpoint).length ? compactByBreakpoint : undefined
    } else {
      next.rglByBreakpoint = undefined
    }
    return next
  })

/**
 * Serializa `configuration` respetando el límite del backend:
 * 1. Layout compacto con `rglByBreakpoint` (rects mínimos).
 * 2. Si aun así excede el límite, degrada quitando `rglByBreakpoint`
 *    (el responsive se re-deriva por escalado desde el `rgl` legacy).
 */
export const buildWorkspaceConfiguration = (layout: WorkspaceLayout): string => {
  const withBreakpoints = JSON.stringify({ data: { layout: compactLayoutForPersistence(layout, true) } })
  if (withBreakpoints.length <= CONFIGURATION_MAX_LENGTH) return withBreakpoints

  return JSON.stringify({ data: { layout: compactLayoutForPersistence(layout, false) } })
}
